// FlagButton — small, unobtrusive flag/rate widget for any AI-generated item.
// Per陛下 19AQ: "给用户一个 flag 的功能，在每一个 ai 生成的内容处都加上，
// 就是可以 flag 是否有误，以及可以让用户打分，但这个不需要醒目，一个小按钮
// 即可。我们未来可以做一个专门的幻觉/错误 check 的组件，但这是后期了。"
//
// UX:
//   - Tiny flag icon button. Default opacity 35% (background-friendly).
//   - Click → small inline popover with: ⚐ flag toggle · 1-5 star rating · note textarea.
//   - When flagged or rated, opacity → 100% + amber accent so user can see it
//     was acted on.
//   - Persists to progress.itemFeedback[`${itemId}.${field}`].

import { useState } from 'react';
import { Flag, Star, X, Send, Sparkles } from 'lucide-react';
import { feedbackKey } from '@examforge/shared/schemas';
import type { ItemFeedback } from '@examforge/shared/schemas';
// Isomorphism Phase 3: Zustand primary (replacing Context).
import { useStoreCourseData } from '../lib/hooks/use-store-course.js';
import { updateProgress, recordEvent } from '../storage/idb.js';
import { useT } from '../i18n/index.js';
import { RepairDialog } from './repair-dialog.js';
import type { RepairableKind } from '../api/repair.js';

export interface FlagButtonProps {
  /** Item id (mcq.id / flashcard.id / etc). */
  itemId: string;
  /** Field name on the item (mirrors ItemOverride.field — usually 'explanation' /
   *  'back' / 'modelAnswer' / 'content' / 'prompt'). For "the whole item" use
   *  '_'. */
  field: string;
  /** 19AU: when set, the popover offers an "AI 修复" button that opens
   *  RepairDialog. Required pieces: kind (so the LLM knows which schema to
   *  enforce) + the current resolved item JSON to send as input. */
  repairable?: {
    kind: RepairableKind;
    itemJson: unknown;
    contextItems?: unknown[];
    sourceSlice?: string;
  };
}

export function FlagButton({ itemId, field, repairable }: FlagButtonProps) {
  const { draft, progress, refreshProgress } = useStoreCourseData();
  const t = useT();
  const [open, setOpen] = useState(false);
  const [repairOpen, setRepairOpen] = useState(false);

  const key = feedbackKey(itemId, field);
  const existing = progress.itemFeedback?.[key];
  const isActive = Boolean(existing && (existing.flagged || existing.rating !== undefined || existing.note));

  const [flagged, setFlagged] = useState(existing?.flagged ?? false);
  const [rating, setRating] = useState<ItemFeedback['rating']>(existing?.rating);
  const [note, setNote] = useState(existing?.note ?? '');

  async function save(): Promise<void> {
    const at = new Date().toISOString();
    const trimmed = note.trim();
    const isEmpty = !flagged && rating === undefined && trimmed.length === 0;
    const previouslyExisted = Boolean(existing);

    await updateProgress(draft.id, (p) => {
      if (!p.itemFeedback) p.itemFeedback = {};
      // If everything is empty / unset, drop the entry entirely.
      if (isEmpty) {
        delete p.itemFeedback[key];
        return;
      }
      const newFeedback: ItemFeedback = {
        flagged,
        ...(rating !== undefined ? { rating } : {}),
        ...(trimmed.length > 0 ? { note: trimmed } : {}),
        updatedAt: at,
      };
      p.itemFeedback[key] = newFeedback;
    });
    await refreshProgress();

    // Session 19AS · G10: append to event log so the operator dashboard sees
    // the chronological feedback stream (itemFeedback alone only carries the
    // latest state). Categorize: 'flag' / 'rating' / 'note' depending on
    // which dimension changed. Cleared-out entry → still log so we know the
    // user retracted feedback (helps detect tooling bugs).
    let evType: 'flag' | 'rating' | 'note' = 'flag';
    if (!flagged && rating !== undefined) evType = 'rating';
    else if (!flagged && rating === undefined && trimmed.length > 0) evType = 'note';
    const summary = isEmpty
      ? `feedback cleared (was: ${previouslyExisted ? 'set' : 'unset'})`
      : `${flagged ? '🚩 flagged' : ''}${rating !== undefined ? ` ★${rating}` : ''}${trimmed.length > 0 ? ` · note(${trimmed.length}c)` : ''}`.trim();
    void recordEvent({
      type: evType,
      summary,
      courseId: draft.id,
      itemId,
      itemField: field,
      viewPath: typeof window !== 'undefined' ? window.location.pathname : undefined,
      payload: {
        flagged,
        rating: rating ?? null,
        noteLength: trimmed.length,
        note: trimmed.length > 0 ? trimmed.slice(0, 500) : undefined,
        previouslyExisted,
        cleared: isEmpty,
      },
    });

    setOpen(false);
  }

  return (
    <span className={`flag-button-wrap${isActive ? ' is-active' : ''}`}>
      <button
        type="button"
        className={`flag-button${isActive ? ' is-active' : ''}`}
        onClick={() => setOpen((o) => !o)}
        aria-label={t('flag.ariaLabel')}
        title={isActive ? t('flag.titleActive') : t('flag.titleIdle')}
      >
        <Flag size={11} aria-hidden="true" />
      </button>
      {open && (
        <div className="flag-popover" role="dialog">
          <div className="flag-popover-head">
            <span className="flag-popover-title">{t('feedback.title')}</span>
            <button type="button" className="flag-popover-close" onClick={() => setOpen(false)} aria-label={t('common.close')}>
              <X size={11} aria-hidden="true" />
            </button>
          </div>
          <label className="flag-popover-row flag-popover-toggle">
            <input
              type="checkbox"
              checked={flagged}
              onChange={(e) => setFlagged(e.target.checked)}
            />
            <span>{t('flag.categoryWrong')}</span>
          </label>
          <div className="flag-popover-row">
            <span className="flag-popover-label">{t('flag.ratingLabel')}</span>
            <span className="flag-popover-stars">
              {[1, 2, 3, 4, 5].map((s) => {
                const filled = (rating ?? 0) >= s;
                return (
                  <button
                    key={s}
                    type="button"
                    className={`flag-star${filled ? ' is-filled' : ''}`}
                    onClick={() => setRating(rating === s ? undefined : (s as ItemFeedback['rating']))}
                    aria-label={t('flag.starAria', { count: s })}
                  >
                    <Star size={13} aria-hidden="true" fill={filled ? 'currentColor' : 'none'} />
                  </button>
                );
              })}
            </span>
          </div>
          <textarea
            className="flag-popover-note"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder={t('flag.notePlaceholder')}
            rows={3}
          />
          <div className="flag-popover-actions">
            {repairable && (
              <button
                type="button"
                className="btn-secondary flag-popover-repair-btn"
                onClick={() => { setOpen(false); setRepairOpen(true); }}
                title={t('flag.repairTitle')}
              >
                <Sparkles size={11} aria-hidden="true" /> {t('flag.repairAction')}
              </button>
            )}
            <button type="button" className="btn-primary" onClick={() => { void save(); }}>
              <Send size={11} aria-hidden="true" /> {t('common.save')}
            </button>
          </div>
        </div>
      )}
      {repairable && (
        <RepairDialog
          open={repairOpen}
          onClose={() => setRepairOpen(false)}
          kind={repairable.kind}
          itemId={itemId}
          itemJson={repairable.itemJson}
          {...(repairable.contextItems ? { contextItems: repairable.contextItems } : {})}
          {...(repairable.sourceSlice ? { sourceSlice: repairable.sourceSlice } : {})}
        />
      )}
    </span>
  );
}
